import React from 'react';
import PropTypes from 'prop-types';

/**
 * Ruhige Listenzeile (Okt 2026, Motion-Kit `.eo-row`): links ein Avatar oder
 * Icon, in der Mitte Titel und Meta-Zeile, rechts etwas Kleines (Pille,
 * Badge, Pfeil). Zeilen untereinander trennt eine Haarlinie — keine Kaesten.
 *
 * Mit `onClick` (oder `href` ueber `as`) wird die ganze Zeile tippbar.
 * Die Trennlinie setzt der Container: `divide-y divide-border` oder die
 * Zeilen selbst mit `divided`.
 */
const ListRow = ({
  leading,
  title,
  meta,
  trailing,
  onClick,
  as,
  divided = false,
  className = '',
  ...rest
}) => {
  const Tag = as || (onClick ? 'button' : 'div');
  const interactive = Boolean(onClick) || Tag === 'a' || typeof Tag !== 'string';
  return (
    <Tag
      {...(Tag === 'button' ? { type: 'button' } : {})}
      onClick={onClick}
      className={[
        'flex w-full items-center gap-3 px-2 py-2.5 text-left',
        divided ? '[&+&]:border-t [&+&]:border-border' : '',
        interactive
          ? 'min-h-[56px] touch-manipulation rounded-xl transition-colors duration-fast hover:bg-foreground/[0.03] ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
          : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-title-s font-semibold leading-tight text-foreground">{title}</span>
        {meta && <span className="mt-0.5 block truncate text-meta text-muted-foreground">{meta}</span>}
      </span>
      {trailing && <span className="ml-auto flex shrink-0 items-center">{trailing}</span>}
    </Tag>
  );
};

ListRow.propTypes = {
  leading: PropTypes.node,
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  trailing: PropTypes.node,
  onClick: PropTypes.func,
  as: PropTypes.elementType,
  divided: PropTypes.bool,
  className: PropTypes.string,
};

export default ListRow;
